import { io } from "socket.io-client";
import api from "../api/client";
import { API_BASE } from "../api/config";
import type { AddressWithProviders } from "../types/models";
import type { ScrapingInitResult, ScrapingSocketConnection, ScrapingSocketHandlers } from "../types/scraping";
import type { AddressProviderTreeNode, LogChunk, SocketAuth, WsLogEvent } from "../types/socket";

const connectScrapingSocket = (jobId: string, handlers: ScrapingSocketHandlers, auth?: SocketAuth): ScrapingSocketConnection => {
  const socket = io(API_BASE, {
    transports: ["websocket"],
    withCredentials: true,
    auth,
  });

  socket.on("connect", () => {
    socket.emit("join", `scraping:${jobId}`);
  });

  const cleanup = () => {
    socket.emit("leave", `scraping:${jobId}`);
    socket.disconnect();
  };

  const onMessage = (data: unknown) => {
    const event = data as WsLogEvent;
    if (event.type === "log" && event.status && event.address && event.provider) {
      handlers.onLog({
        address: event.address,
        provider: event.provider,
        status: event.status as LogChunk["status"],
        message: event.message || "",
        key: event.key,
        params: event.params,
        ts: event.ts,
      });
    } else if (event.type === "done") {
      handlers.onDone();
      cleanup();
    } else if (event.type === "error") {
      handlers.onError(event.message || "Scraping failed");
      cleanup();
    }
  };

  socket.on("message", onMessage);

  socket.on("connect_error", () => {
    handlers.onError("Connection failed");
    cleanup();
  });

  return { disconnect: cleanup };
};

const buildAddressTree = (addressesData: AddressWithProviders[]): AddressProviderTreeNode[] =>
  addressesData.map((addr) => ({
    addressName: addr.name,
    providers: addr.providers.map((prov) => ({
      name: prov.provider.name,
      id: prov.id,
    })),
  }));

const initScrapingJob = async (): Promise<ScrapingInitResult> => {
  const [addressesData, { jobId }] = await Promise.all([api.addresses.list(), api.actions.startScraping()]);
  return { jobId, addressTree: buildAddressTree(addressesData) };
};

export { connectScrapingSocket, buildAddressTree, initScrapingJob };
